import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MainLayout } from '@/components/Layout';
import { GoogleMap, LoadScript, Marker, InfoWindow, Circle } from '@react-google-maps/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api, { drivers as driversApi } from '@/lib/api';
import {
  RefreshCw, MapPin, Truck, User, Package, Phone,
  Navigation, CheckCircle2, Search, X, Bike, UserCheck, UserX,
  DollarSign, AlertTriangle, Clock, ChevronRight, LocateFixed, Zap
} from 'lucide-react';

const GOOGLE_MAPS_API_KEY = 'AIzaSyBslmdWaiWkD85mWtZY8loRATqVgKM_K6g';

const STATUS = {
  PREPARO: { label: 'Preparo', color: '#F59E0B', bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
  PRONTO: { label: 'Pronto', color: '#22C55E', bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  CONFIRMADO: { label: 'Confirmado', color: '#3B82F6', bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
  SAIU: { label: 'Em Rota', color: '#8B5CF6', bg: 'bg-violet-500/10', text: 'text-violet-400', border: 'border-violet-500/20' },
};

const mapContainerStyle = { width: '100%', height: '100%' };

const darkMapStyle = [
  { elementType: 'geometry', stylers: [{ color: '#1a1a2e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1a1a2e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8b8b8b' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#2a2a4a' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#1a2a1a' }, { visibility: 'on' }] },
  { featureType: 'poi.park', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2a2a4a' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#1a1a2e' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3a3a5a' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1a1a2e' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0a0a1a' }] },
];

const mapOptions = {
  styles: darkMapStyle,
  disableDefaultUI: true,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
};

function DeliveryCard({ order, isSelected, isChecked, onSelect, onCheck, onRecalcCoords }) {
  const st = STATUS[order._st] || STATUS.PREPARO;
  return (
    <div className={`group relative rounded-2xl transition-all duration-200 ${isSelected ? 'bg-gradient-to-br from-indigo-500/20 to-violet-500/20 ring-2 ring-indigo-500/50' : isChecked ? 'bg-violet-500/10 ring-1 ring-violet-500/30' : 'bg-slate-800/40 hover:bg-slate-800/60'}`}>
      <div className="flex items-start gap-3 p-4">
        <div className="pt-1">
          <input type="checkbox" checked={isChecked} onChange={() => onCheck(order.order_id)} className="w-5 h-5 rounded-lg border-2 border-slate-600 bg-slate-900 text-indigo-500 focus:ring-indigo-500/30 cursor-pointer"/>
        </div>
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onSelect(order)}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-white">#{order.order_number || order.order_id?.slice(-6)}</span>
              {(order.payment_mode === 'COD' || order.payment_mode === 'DINHEIRO') && (
                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px] px-2">
                  <DollarSign className="w-3 h-3 mr-0.5" />COD
                </Badge>
              )}
            </div>
            <Badge className={`${st.bg} ${st.text} ${st.border} border text-[10px] font-semibold`}>{st.label}</Badge>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-slate-700/50 flex items-center justify-center">
              <User className="w-4 h-4 text-slate-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-white truncate">{order.customer_name || 'Sem nome'}</p>
              {order.customer_phone && <p className="text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" />{order.customer_phone}</p>}
            </div>
          </div>
          <div className="flex items-start gap-2 mb-3">
            <MapPin className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-slate-400 line-clamp-2">{order.address_text || order.neighborhood || 'Endereço não informado'}</p>
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-slate-700/50">
            <span className="text-base font-bold text-emerald-400">R$ {Number(order.total||0).toFixed(2)}</span>
            <div className="flex items-center gap-2">
              {!order.has_coordinates ? (
                <button onClick={(e) => { e.stopPropagation(); onRecalcCoords(order); }} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 text-xs font-medium transition-colors">
                  <AlertTriangle className="w-3.5 h-3.5" />Sem GPS
                </button>
              ) : (
                <button onClick={(e) => { e.stopPropagation(); onRecalcCoords(order); }} className="p-1.5 rounded-lg bg-slate-700/50 hover:bg-slate-600/50 transition-colors" title="Recalcular coordenadas">
                  <RefreshCw className="w-3.5 h-3.5 text-slate-400" />
                </button>
              )}
              <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-slate-400 transition-colors" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DriverCard({ driver, activeDeliveries, onToggle }) {
  const deliveryCount = activeDeliveries.filter(d => d.driver_id === driver.id).length;
  return (
    <div className={`rounded-2xl transition-all duration-200 ${driver.is_available ? 'bg-emerald-500/5 ring-1 ring-emerald-500/20' : 'bg-slate-800/30'}`}>
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${driver.is_available ? 'bg-gradient-to-br from-emerald-500/20 to-green-500/20 ring-1 ring-emerald-500/30' : 'bg-slate-800'}`}>
              <Bike className={`w-6 h-6 ${driver.is_available ? 'text-emerald-400' : 'text-slate-600'}`} />
            </div>
            <div>
              <p className="font-semibold text-white">{driver.name}</p>
              <p className="text-xs text-slate-500">{driver.phone}</p>
              {deliveryCount > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  <Package className="w-3 h-3 text-amber-400" />
                  <span className="text-[10px] text-amber-400 font-medium">{deliveryCount} entrega(s)</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${driver.is_available ? 'bg-emerald-400 animate-pulse' : 'bg-slate-700'}`} />
            <button onClick={() => onToggle(driver)} className={`p-2 rounded-xl transition-colors ${driver.is_available ? 'bg-amber-500/10 hover:bg-amber-500/20 text-amber-400' : 'bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400'}`}>
              {driver.is_available ? <UserX className="w-5 h-5" /> : <UserCheck className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActiveDeliveryCard({ delivery, onFlyTo }) {
  const statusConfig = {
    ASSIGNED: { label: 'Atribuído', bg: 'bg-blue-500/10', text: 'text-blue-400' },
    OUT_FOR_DELIVERY: { label: 'Em Rota', bg: 'bg-amber-500/10', text: 'text-amber-400' },
    ARRIVED: { label: 'No Local', bg: 'bg-violet-500/10', text: 'text-violet-400' },
  };
  const st = statusConfig[delivery.assignment_status] || statusConfig.ASSIGNED;
  return (
    <div onClick={() => delivery.lat && delivery.lng && onFlyTo({ lat: delivery.lat, lng: delivery.lng })} className="rounded-2xl bg-gradient-to-br from-slate-800/60 to-slate-900/60 p-4 cursor-pointer hover:from-slate-800/80 transition-all ring-1 ring-white/5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-bold text-white">#{delivery.order_number || delivery.order_id?.slice(0, 8)}</span>
        <Badge className={`${st.bg} ${st.text} text-[10px] font-semibold`}>{st.label}</Badge>
      </div>
      <p className="text-sm font-medium text-white mb-1">{delivery.customer_name}</p>
      <p className="text-xs text-slate-500 mb-3 line-clamp-2">{delivery.address}</p>
      <div className="flex items-center justify-between pt-3 border-t border-slate-700/50">
        <div className="flex items-center gap-2">
          <Bike className="w-4 h-4 text-emerald-400" />
          <span className="text-xs text-emerald-400 font-medium">{delivery.driver_name}</span>
        </div>
        <span className="text-sm font-bold text-emerald-400">R$ {Number(delivery.total||0).toFixed(2)}</span>
      </div>
    </div>
  );
}


// Trata erros do FastAPI (Pydantic retorna detail como array de objetos)
function apiError(e, fallback = 'Erro') {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(' · ');
  if (d && typeof d === 'object') return d.msg || JSON.stringify(d);
  return e?.message || fallback;
}

export default function RoutingPage() {
  const [orders, setOrders] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [driverLocations, setDriverLocations] = useState([]);
  const [activeDeliveries, setActiveDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('PRONTO');
  const [tab, setTab] = useState('orders');
  const [showAssign, setShowAssign] = useState(false);
  const [assignId, setAssignId] = useState(null);
  const [driverId, setDriverId] = useState('');
  const [checked, setChecked] = useState([]);
  const [showRoute, setShowRoute] = useState(false);
  const [routeData, setRouteData] = useState(null);
  const [storeLocation, setStoreLocation] = useState({ lat: -23.5314103, lng: -46.7980872 });
  const [mapCenter, setMapCenter] = useState({ lat: -23.5314103, lng: -46.7980872 });
  const [mapZoom, setMapZoom] = useState(13);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const mapRef = useRef(null);
  const geocodeCache = useRef({});

  const geocodeAddress = useCallback(async (text) => {
    if (!text) return null;
    if (geocodeCache.current[text]) return geocodeCache.current[text];
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(text + ', Brasil')}&key=${GOOGLE_MAPS_API_KEY}`
      );
      const data = await response.json();
      if (data.results && data.results.length > 0) {
        const location = data.results[0].geometry.location;
        const coords = { lat: location.lat, lng: location.lng };
        geocodeCache.current[text] = coords;
        return coords;
      }
    } catch (err) {
      console.error('Geocoding error:', err);
    }
    return null;
  }, []);

  const recalculateCoords = async (order) => {
    if (!order.address_text) { toast.error('Endereço não disponível'); return; }
    toast.info('Buscando coordenadas...');
    try {
      const coords = await geocodeAddress(order.address_text);
      if (coords) {
        try { await api.patch(`/delivery/orders/${order.delivery_id}/update-coords`, coords); } catch {}
        setOrders(prev => prev.map(o => o.order_id === order.order_id ? { ...o, lat: coords.lat, lng: coords.lng, has_coordinates: true } : o));
        toast.success('Coordenadas atualizadas!');
        setMapCenter(coords);
        setMapZoom(16);
      } else { toast.error('Não foi possível encontrar coordenadas'); }
    } catch { toast.error('Erro ao buscar coordenadas'); }
  };

  const load = useCallback(async () => {
    try {
      const [pr, dr, loc, deliv] = await Promise.all([
        api.get('/cod/routes/eligible-orders', { params: { order_status: 'PRONTO' } }).catch(() => ({ data: [] })),
        driversApi.list(),
        driversApi.getLocations().catch(() => ({ data: { drivers: [] } })),
        driversApi.getActiveDeliveries().catch(() => ({ data: { deliveries: [] } })),
      ]);
      const all = (pr.data || []).map(o => ({ ...o, _st: 'PRONTO' }));
      
      // Geocode addresses without coordinates
      const toGeocode = all.filter(o => !o.has_coordinates && o.address_text);
      if (toGeocode.length > 0) {
        const results = await Promise.all(toGeocode.map(async o => {
          const coords = await geocodeAddress(o.address_text);
          return { id: o.order_id, coords };
        }));
        results.forEach(({ id, coords }) => {
          if (coords) {
            const order = all.find(o => o.order_id === id);
            if (order) { order.lat = coords.lat; order.lng = coords.lng; order.has_coordinates = true; }
          }
        });
      }
      
      setOrders(all);
      setAllDrivers(dr.data || []);
      setDriverLocations(loc.data?.drivers || []);
      setActiveDeliveries(deliv.data?.deliveries || []);
    } catch { toast.error('Erro ao carregar'); }
    finally { setLoading(false); }
  }, [geocodeAddress]);

  useEffect(() => {
    api.get('/store/location').then(r => {
      const loc = { lat: r.data.latitude, lng: r.data.longitude };
      setStoreLocation(loc);
      setMapCenter(loc);
    }).catch(e => console.error('Erro ao buscar coordenadas da loja:', e));
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const filtered = orders.filter(o => {
    if (o._st !== filter) return false;
    if (search) {
      const s = search.toLowerCase();
      return (o.customer_name||'').toLowerCase().includes(s) || (o.order_id||'').includes(s) || (o.neighborhood||'').toLowerCase().includes(s);
    }
    return true;
  });

  const cnt = { PREPARO: 0, PRONTO: 0, CONFIRMADO: 0, SAIU: 0 };
  orders.forEach(o => { cnt[o._st] = (cnt[o._st]||0) + 1; });

  const pick = (o) => {
    setSelected(o);
    if (o.lat && o.lng) {
      setMapCenter({ lat: o.lat, lng: o.lng });
      setMapZoom(16);
    }
  };

  const assign = async () => {
    if (!assignId || !driverId) return;
    try {
      await api.patch(`/delivery/orders/${assignId}/assign`, null, { params: { driver_id: driverId } });
      toast.success('Motoboy atribuído!');
      setShowAssign(false);
      load();
    } catch (e) { toast.error(apiError(e, 'Erro')); }
  };

  const toggleCheck = id => setChecked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const suggestRoute = async () => {
    if (checked.length < 1) return toast.error('Selecione pedidos');
    try {
      const r = await api.post('/cod/routes/suggest', { order_ids: checked, constraints: { max_stops_per_route: 10, average_speed_kmh: 25 } });
      setRouteData(r.data);
      setShowRoute(true);
    } catch (e) { toast.error(apiError(e, 'Erro ao roteirizar')); }
  };

  const confirmRoute = async (sug) => {
    try {
      await api.post('/cod/routes/confirm', {
        routes: [{
          temp_route_key: sug.temp_route_key,
          driver_id: driverId || null,
          stops: sug.stops.map(s => ({ order_id: s.order_id, sequence: s.sequence }))
        }]
      });
      toast.success('Rota confirmada!');
      setShowRoute(false);
      setChecked([]);
      load();
    } catch (e) { toast.error(apiError(e, 'Erro')); }
  };

  const toggleDriver = async (d) => {
    try {
      await driversApi.toggleAvailability(d.id);
      toast.success(d.is_available ? 'Offline' : 'Online');
      load();
    } catch { toast.error('Erro'); }
  };

  const onMapLoad = useCallback((map) => { mapRef.current = map; }, []);

  const centerOnStore = () => {
    setMapCenter(storeLocation);
    setMapZoom(13);
  };

  return (
    <MainLayout>
      <div className="h-[calc(100vh-64px)] flex flex-col -m-6 md:-m-8" data-testid="routing-map-page">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 bg-gradient-to-r from-slate-900 via-slate-900 to-slate-800 border-b border-white/5">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/25">
                <Navigation className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">Central de Entregas</h1>
                <p className="text-xs text-slate-500">Google Maps</p>
              </div>
            </div>
            <div className="hidden lg:flex items-center gap-3">
              {Object.entries(cnt).filter(([k,v]) => v > 0).map(([k,v]) => {
                const st = STATUS[k];
                return (
                  <div key={k} className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${st.bg} ${st.border} border`}>
                    <span className={`text-xs font-semibold ${st.text}`}>{v} {st.label}</span>
                  </div>
                );
              })}
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-800/60 border border-slate-700/50">
                <Bike className="w-4 h-4 text-emerald-400" />
                <span className="text-xs text-slate-300">{allDrivers.filter(d=>d.is_available).length} online</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {checked.length > 0 && (
              <Button onClick={suggestRoute} className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white shadow-lg h-10 px-4">
                <Zap className="w-4 h-4 mr-2" /> Roteirizar {checked.length}
              </Button>
            )}
            <Button variant="ghost" onClick={() => { setLoading(true); load(); }} className="text-slate-400 hover:text-white h-10 w-10 p-0">
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Sidebar */}
          <div className="w-full lg:w-[380px] md:w-[340px] flex-shrink-0 bg-gradient-to-b from-slate-900 to-slate-950 border-r border-white/5 flex flex-col">
            <Tabs value={tab} onValueChange={setTab} className="flex flex-col flex-1 min-h-0">
              <TabsList className="flex-shrink-0 grid w-full grid-cols-2 bg-transparent rounded-none h-14 p-2 gap-2 border-b border-white/5">
                <TabsTrigger value="orders" className="rounded-xl data-[state=active]:bg-indigo-500/10 data-[state=active]:text-indigo-400 text-slate-500 font-semibold h-10">
                  <Package className="w-4 h-4 mr-2" /> Entregas ({orders.length})
                </TabsTrigger>
                <TabsTrigger value="drivers" className="rounded-xl data-[state=active]:bg-indigo-500/10 data-[state=active]:text-indigo-400 text-slate-500 font-semibold h-10">
                  <Bike className="w-4 h-4 mr-2" /> Motoboys ({allDrivers.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="orders" className="flex-1 flex flex-col mt-0 overflow-hidden min-h-0">
                <div className="flex-shrink-0 p-4 space-y-3 border-b border-white/5">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                    <Input placeholder="Buscar cliente, pedido..." value={search} onChange={e => setSearch(e.target.value)}
                      className="pl-10 bg-slate-800/50 border-white/5 text-white placeholder:text-slate-600 h-11 rounded-xl" />
                  </div>
                  <div className="flex gap-2">
                    {[{ k: 'PRONTO', l: 'Pronto' }, { k: 'SAIU', l: 'Em Rota' }].map(f => (
                      <button key={f.k} onClick={() => setFilter(f.k)}
                        className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${filter === f.k ? 'bg-indigo-600 text-white shadow-lg' : 'bg-slate-800/50 text-slate-400 hover:text-white'}`}>
                        {f.l} {cnt[f.k] > 0 ? `(${cnt[f.k]})` : ''}
                      </button>
                    ))}
                  </div>
                </div>
                <ScrollArea className="flex-1 min-h-0">
                  <div className="p-4 space-y-3">
                    {loading && !orders.length ? (
                      <div className="flex flex-col items-center justify-center py-20 gap-4">
                        <div className="w-12 h-12 rounded-full border-2 border-indigo-500/20 border-t-indigo-500 animate-spin" />
                        <span className="text-sm text-slate-600">Carregando...</span>
                      </div>
                    ) : filter === 'SAIU' ? (
                      activeDeliveries.length === 0 ? (
                        <div className="text-center py-20 text-slate-600">
                          <Bike className="w-12 h-12 mx-auto mb-4 opacity-30" />
                          <p className="font-medium">Nenhuma entrega em rota</p>
                        </div>
                      ) : activeDeliveries.map(d => (
                        <ActiveDeliveryCard key={d.order_id} delivery={d} onFlyTo={(pos) => { setMapCenter(pos); setMapZoom(16); }} />
                      ))
                    ) : filtered.length === 0 ? (
                      <div className="text-center py-20 text-slate-600">
                        <Truck className="w-12 h-12 mx-auto mb-4 opacity-30" />
                        <p className="font-medium">Nenhuma entrega</p>
                      </div>
                    ) : filtered.map(o => (
                      <DeliveryCard key={o.order_id} order={o} isSelected={selected?.order_id === o.order_id} isChecked={checked.includes(o.order_id)} onSelect={pick} onCheck={toggleCheck} onRecalcCoords={recalculateCoords} />
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="drivers" className="flex-1 flex flex-col mt-0 overflow-hidden min-h-0">
                <ScrollArea className="flex-1 min-h-0">
                  <div className="p-4 space-y-3">
                    {allDrivers.length === 0 ? (
                      <div className="text-center py-20 text-slate-600">
                        <User className="w-12 h-12 mx-auto mb-4 opacity-30" />
                        <p className="font-medium">Nenhum motoboy</p>
                      </div>
                    ) : allDrivers.map(d => (
                      <DriverCard key={d.id} driver={d} activeDeliveries={activeDeliveries} onToggle={toggleDriver} />
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </div>

          {/* Map */}
          <div className="flex-1 flex flex-col min-h-0 min-w-0 bg-slate-950 relative">
            <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={mapCenter}
                zoom={mapZoom}
                options={mapOptions}
                onLoad={onMapLoad}
              >
                {/* Store Marker */}
                <Marker
                  position={storeLocation}
                  icon={{
                    path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                    scale: 12,
                    fillColor: '#FF6B6B',
                    fillOpacity: 1,
                    strokeColor: '#fff',
                    strokeWeight: 3,
                  }}
                  onClick={() => setSelectedMarker({ type: 'store', data: { name: 'Hit Grill', address: 'Av. Hildebrando de Lima, 699 - Osasco' } })}
                />
                <Circle center={storeLocation} radius={300} options={{ fillColor: '#FF6B6B', fillOpacity: 0.1, strokeColor: '#FF6B6B', strokeWeight: 1 }} />

                {/* Order Markers */}
                {filtered.filter(o => o.has_coordinates && o.lat && o.lng).map(o => {
                  const st = STATUS[o._st] || STATUS.PREPARO;
                  const isChk = checked.includes(o.order_id);
                  return (
                    <Marker
                      key={o.order_id}
                      position={{ lat: o.lat, lng: o.lng }}
                      icon={{
                        path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                        scale: 10,
                        fillColor: isChk ? '#A855F7' : st.color,
                        fillOpacity: 1,
                        strokeColor: '#fff',
                        strokeWeight: 2,
                      }}
                      onClick={() => { setSelectedMarker({ type: 'order', data: o }); pick(o); }}
                    />
                  );
                })}

                {/* Driver Markers */}
                {driverLocations.filter(d => d.lat && d.lng).map(d => (
                  <Marker
                    key={`driver-${d.driver_id}`}
                    position={{ lat: parseFloat(d.lat), lng: parseFloat(d.lng) }}
                    icon={{
                      path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW || 2,
                      scale: 6,
                      fillColor: '#10B981',
                      fillOpacity: 1,
                      strokeColor: '#fff',
                      strokeWeight: 2,
                      rotation: 0,
                    }}
                    onClick={() => setSelectedMarker({ type: 'driver', data: d })}
                  />
                ))}

                {/* Active Delivery Markers */}
                {activeDeliveries.filter(d => d.lat && d.lng).map(d => (
                  <Marker
                    key={`active-${d.order_id}`}
                    position={{ lat: parseFloat(d.lat), lng: parseFloat(d.lng) }}
                    icon={{
                      path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                      scale: 8,
                      fillColor: d.assignment_status === 'OUT_FOR_DELIVERY' ? '#F59E0B' : '#3B82F6',
                      fillOpacity: 1,
                      strokeColor: '#fff',
                      strokeWeight: 2,
                    }}
                    onClick={() => setSelectedMarker({ type: 'activeDelivery', data: d })}
                  />
                ))}

                {/* InfoWindow */}
                {selectedMarker && (() => {
                  const pos = selectedMarker.type === 'store'
                    ? storeLocation
                    : { lat: parseFloat(selectedMarker.data?.lat), lng: parseFloat(selectedMarker.data?.lng) };
                  if (!isFinite(pos.lat) || !isFinite(pos.lng)) return null;
                  return (
                  <InfoWindow
                    position={pos}
                    onCloseClick={() => setSelectedMarker(null)}
                  >
                    <div className="p-2 min-w-[200px]">
                      {selectedMarker.type === 'store' && (
                        <>
                          <h3 className="font-bold text-red-500">Hit Grill</h3>
                          <p className="text-sm text-gray-600">Av. Hildebrando de Lima, 699</p>
                        </>
                      )}
                      {selectedMarker.type === 'order' && (
                        <>
                          <h3 className="font-bold">#{selectedMarker.data.order_number || selectedMarker.data.order_id?.slice(-6)}</h3>
                          <p className="font-medium">{selectedMarker.data.customer_name}</p>
                          <p className="text-sm text-gray-600">{selectedMarker.data.address_text}</p>
                          <p className="text-green-600 font-bold mt-2">R$ {Number(selectedMarker.data.total||0).toFixed(2)}</p>
                          <button
                            onClick={() => { setAssignId(selectedMarker.data.delivery_id); setShowAssign(true); setSelectedMarker(null); }}
                            className="mt-2 w-full py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-500"
                          >
                            Atribuir Motoboy
                          </button>
                        </>
                      )}
                      {selectedMarker.type === 'driver' && (
                        <>
                          <h3 className="font-bold text-emerald-600 text-base">
                            {selectedMarker.data?.driver_name || 'Motoboy'}
                          </h3>
                          <p className="text-sm text-gray-700">
                            {selectedMarker.data?.driver_phone || '—'}
                          </p>
                          <div className="border-t border-gray-200 mt-2 pt-2 space-y-1">
                            {selectedMarker.data?.vehicle_type && (
                              <p className="text-xs text-gray-600">
                                <span className="font-semibold">Veículo:</span>{' '}
                                {({MOTORCYCLE:'Moto',BIKE:'Bicicleta',CAR:'Carro',WALK:'A pé'})[selectedMarker.data.vehicle_type] || selectedMarker.data.vehicle_type}
                                {selectedMarker.data.vehicle_plate ? ' · ' + selectedMarker.data.vehicle_plate : ''}
                              </p>
                            )}
                            {selectedMarker.data?.vehicle_model && (
                              <p className="text-xs text-gray-600">
                                <span className="font-semibold">Modelo:</span> {selectedMarker.data.vehicle_model}
                              </p>
                            )}
                            <p className="text-xs text-gray-600">
                              <span className="font-semibold">Localização:</span>{' '}
                              {selectedMarker.data?.lat && selectedMarker.data?.lng
                                ? parseFloat(selectedMarker.data.lat).toFixed(5) + ', ' + parseFloat(selectedMarker.data.lng).toFixed(5)
                                : '—'}
                            </p>
                            {selectedMarker.data?.speed_mps != null && (
                              <p className="text-xs text-gray-600">
                                <span className="font-semibold">Velocidade:</span>{' '}
                                {(parseFloat(selectedMarker.data.speed_mps) * 3.6).toFixed(1)} km/h
                              </p>
                            )}
                            {selectedMarker.data?.updated_at && (
                              <p className="text-xs text-gray-500 mt-1">
                                Atualizado: {new Date(selectedMarker.data.updated_at).toLocaleTimeString('pt-BR')}
                              </p>
                            )}
                          </div>
                        </>
                      )}
                      {selectedMarker.type === 'activeDelivery' && (
                        <>
                          <h3 className="font-bold">#{selectedMarker.data.order_number || selectedMarker.data.order_id?.slice(0, 8)}</h3>
                          <p className="font-medium">{selectedMarker.data.customer_name}</p>
                          <p className="text-sm text-gray-600">{selectedMarker.data.address}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Bike className="w-4 h-4 text-emerald-500" />
                            <span className="text-sm text-emerald-600">{selectedMarker.data.driver_name}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </InfoWindow>
                );
                })()}
              </GoogleMap>
            </LoadScript>

            {/* Recenter Button */}
            <button
              onClick={centerOnStore}
              className="absolute top-4 right-4 w-10 h-10 bg-slate-900/90 backdrop-blur border border-white/10 rounded-xl flex items-center justify-center hover:bg-slate-800 transition-all shadow-lg z-10"
              title="Centralizar na loja"
            >
              <LocateFixed className="w-5 h-5 text-white" />
            </button>

            {/* Selected Order Panel */}
            {selected && (
              <div className="absolute bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-xl border-t border-white/10 p-5 z-10">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xl font-bold text-white">#{selected.order_number || selected.order_id?.slice(-6)}</span>
                      <Badge className={`${(STATUS[selected._st]||STATUS.PREPARO).bg} ${(STATUS[selected._st]||STATUS.PREPARO).text}`}>
                        {(STATUS[selected._st]||STATUS.PREPARO).label}
                      </Badge>
                      {(selected.payment_mode === 'COD' || selected.payment_mode === 'DINHEIRO') && (
                        <Badge className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                          <DollarSign className="w-3 h-3 mr-1" /> COD
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-6">
                      <div>
                        <p className="text-[10px] uppercase text-slate-500 mb-1">Cliente</p>
                        <p className="text-sm text-white font-medium">{selected.customer_name}</p>
                        {selected.customer_phone && <p className="text-xs text-slate-500">{selected.customer_phone}</p>}
                      </div>
                      <div className="col-span-2">
                        <p className="text-[10px] uppercase text-slate-500 mb-1">Endereço</p>
                        <p className="text-sm text-slate-300">{selected.address_text}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase text-slate-500 mb-1">Valor</p>
                        <p className="text-2xl text-emerald-400 font-bold">R$ {Number(selected.total||0).toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <Button onClick={() => { setAssignId(selected.delivery_id); setShowAssign(true); }} className="bg-indigo-600 hover:bg-indigo-500 text-white h-11 px-5">
                      <User className="w-4 h-4 mr-2" /> Atribuir
                    </Button>
                    <Button variant="ghost" onClick={() => setSelected(null)} className="text-slate-500 hover:text-white h-11 w-11 p-0">
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Assign Dialog */}
      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent className="bg-slate-900 border-white/10 text-white">
          <DialogHeader><DialogTitle>Atribuir Motoboy</DialogTitle></DialogHeader>
          <Select value={driverId} onValueChange={setDriverId}>
            <SelectTrigger className="bg-slate-800/50 border-white/10 text-white h-12"><SelectValue placeholder="Selecione um motoboy" /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-white/10">
              {allDrivers.filter(d => d.is_available).map(d => (<SelectItem key={d.id} value={String(d.id)} className="text-white">{d.name} ({d.phone})</SelectItem>))}
              {allDrivers.filter(d => !d.is_available).map(d => (<SelectItem key={d.id} value={String(d.id)} className="text-slate-500">{d.name} (offline)</SelectItem>))}
            </SelectContent>
          </Select>
          <DialogFooter className="gap-3">
            <Button variant="ghost" onClick={() => setShowAssign(false)} className="text-slate-400">Cancelar</Button>
            <Button onClick={assign} disabled={!driverId} className="bg-indigo-600 hover:bg-indigo-500">Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Route Dialog */}
      <Dialog open={showRoute} onOpenChange={setShowRoute}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-2xl">
          <DialogHeader><DialogTitle>Sugestão de Rotas</DialogTitle></DialogHeader>
          {routeData?.suggestions?.map((sug, i) => (
            <div key={sug.temp_route_key} className="border border-white/10 rounded-2xl p-5 bg-slate-800/30">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <span className="font-bold text-white text-lg">Rota {i+1}</span>
                  <span className="text-sm text-slate-400 ml-4">{sug.total_stops} paradas - {sug.estimated_km.toFixed(1)}km - ~{sug.estimated_minutes}min</span>
                </div>
                <div className="flex gap-3">
                  <Select value={driverId} onValueChange={setDriverId}>
                    <SelectTrigger className="bg-slate-800/50 border-white/10 text-white w-44 h-9"><SelectValue placeholder="Motoboy" /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-white/10">{allDrivers.filter(d=>d.is_available).map(d => (<SelectItem key={d.id} value={String(d.id)} className="text-white">{d.name}</SelectItem>))}</SelectContent>
                  </Select>
                  <Button onClick={() => confirmRoute(sug)} className="bg-emerald-600 hover:bg-emerald-500 h-9"><CheckCircle2 className="w-4 h-4 mr-2" />Confirmar</Button>
                </div>
              </div>
              <div className="space-y-2">
                {sug.stops?.map((s,j) => (
                  <div key={s.order_id} className="flex items-center gap-4 text-sm text-slate-300 p-3 rounded-xl bg-slate-900/50">
                    <span className="w-7 h-7 rounded-full bg-indigo-600/30 flex items-center justify-center text-xs font-bold text-indigo-400">{j+1}</span>
                    <span className="flex-1 font-medium">{s.customer_name}</span>
                    <span className="text-emerald-400 font-medium">{s.distance_from_prev_km.toFixed(1)}km</span>
                    <span className="text-slate-500">~{s.eta_minutes}min</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <DialogFooter><Button variant="ghost" onClick={() => setShowRoute(false)} className="text-slate-400">Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
